/**
 * F4 (#1858 §4b rule 6): THE public projection of an answer's events, written once in TypeScript — the same rules as
 * the server's `agent/landing/v1/acceptance.py`, checked against it by a backend-written fixture
 * (`__fixtures__/acceptanceProjection.json`, `acceptance.test.ts`).
 *
 * Every `worker_result` of a part a `part_accepted` says was not accepted becomes its public form (its ids, status
 * `not_accepted`; no spec, SQL, columns or rows), and its reviews keep only their type, ids, verdict and a fixed reason
 * code (a gatekeeper's reasons and hint can quote the rejected values). Every other event, and every event of an
 * accepted part, is returned untouched: an answer whose parts were all accepted projects to itself (the same array).
 */
import type { LandingAgentEvent, WorkerResultSnapshot } from './landingAgentSlice';

export const PART = 'part_accepted';
export const NOT_ACCEPTED = 'not_accepted';
/** The server's own sentence for a part that was not accepted (`acceptance.LINE`). */
export const NOT_ACCEPTED_LINE = "These numbers didn't pass the check, so they're not shown.";
export const REVIEWS = ['gatekeeper_verdict', 'retry_queued', 'retry_exhausted', 'fallback_to_prior_attempt'];
const IDS = ['sub_task_id', 'result_id', 'thread_id'];

/** The sub-tasks a `part_accepted` says were not accepted (anything but `accepted: true` is not accepted). */
export function rejected(events: LandingAgentEvent[]): Set<string> {
  return new Set((events || []).filter((e) => e && e.type === PART && typeof e.sub_task_id === 'string'
    && e.accepted !== true).map((e) => e.sub_task_id as string));
}

function ids(event: LandingAgentEvent): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of IDS) if (typeof event[k] === 'string') out[k] = event[k];
  return out;
}

/** One event of a part that was not accepted, as every reader reads it. */
export function publicEvent(event: LandingAgentEvent): LandingAgentEvent {
  if (event.type === 'worker_result') {
    return { type: event.type, ...ids(event), status: NOT_ACCEPTED, error_code: NOT_ACCEPTED };
  }
  if (REVIEWS.includes(event.type)) {
    return { type: event.type, ...ids(event), ...(typeof event.verdict === 'string' ? { verdict: event.verdict } : {}),
      reason_code: NOT_ACCEPTED };
  }
  return event;
}

/** THE projection (module docstring); `parts`: the parts already known not to be accepted, else read from `events`. */
export function project(events: LandingAgentEvent[], parts?: Set<string>): LandingAgentEvent[] {
  const gone = parts || rejected(events);
  if (!gone.size) return events;
  return events.map((e) => (e && typeof e.sub_task_id === 'string' && gone.has(e.sub_task_id)
    && (e.type === 'worker_result' || REVIEWS.includes(e.type)) ? publicEvent(e) : e));
}

/** A part's worker snapshot once it was not accepted: its id and status only, never a row, the SQL or a reason. */
export function publicSnapshot(subTaskId: string): WorkerResultSnapshot {
  return { sub_task_id: subTaskId, status: NOT_ACCEPTED };
}
